/**
 * Vidu S1 Live client for BetUDontBet.
 * Create session via edge (no API key) → control WS proxy → optional AliRTC media.
 *
 * Docs: https://platform.vidu.com/docs/vidu-s1
 */

export type ViduRtc = {
  app_id?: string;
  channel_id?: string;
  user_id?: string;
  token?: string;
  token_expire_at?: string;
};

export type ViduLiveSession = {
  liveId: string;
  status: string;
  rtc: ViduRtc | null;
  controlWsPath: string;
  callMode: "video" | "audio";
};

export type ViduLiveController = {
  session: ViduLiveSession;
  ready: boolean;
  sendText: (content: string) => void;
  hangup: () => void;
  destroy: () => void;
};

const MSG = {
  CONN_INIT: 1,
  CONN_INIT_ACK: 2,
  CALL_HANGUP: 5,
  FORCE_HANGUP: 6,
  AUDIO_INTERRUPTED: 7,
  TEXT_MSG: 99,
} as const;

function wsUrl(path: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Start a Live session (Worker creates with persona + knowledge).
 * persona: budb (HU table talk) | racalvin (blackjack dealer)
 */
export async function createViduLiveSession(opts?: {
  /** audio (default, fast) | video (slow ~90s, may fail on edge) */
  callMode?: "video" | "audio";
  persona?: "budb" | "racalvin" | string;
}): Promise<ViduLiveSession> {
  const r = await fetch("/api/vidu/live", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      call_mode: opts?.callMode || "audio",
      persona: opts?.persona || "budb",
    }),
  });
  const j = (await r.json()) as {
    success?: boolean;
    error?: string;
    live?: { id?: string | number; status?: string; call_mode?: string };
    rtc?: ViduRtc | null;
    controlWsPath?: string | null;
    persona?: string;
  };
  if (!r.ok || !j.success) {
    throw new Error(j.error || `Vidu live failed (${r.status})`);
  }
  const liveId = j.live?.id != null ? String(j.live.id) : "";
  if (!liveId || !j.controlWsPath) {
    throw new Error("Vidu response missing live_id / controlWsPath");
  }
  return {
    liveId,
    status: String(j.live?.status || "waiting"),
    rtc: j.rtc || null,
    controlWsPath: j.controlWsPath,
    callMode: (j.live?.call_mode as "video" | "audio") || "video",
  };
}

/** @deprecated use createViduLiveSession({ persona: "budb" }) */
export async function createBudbViduLive(opts?: {
  callMode?: "video" | "audio";
}): Promise<ViduLiveSession> {
  return createViduLiveSession({ ...opts, persona: "budb" });
}

export async function createRacalvinViduLive(opts?: {
  callMode?: "video" | "audio";
}): Promise<ViduLiveSession> {
  return createViduLiveSession({ ...opts, persona: "racalvin" });
}

/**
 * Open control WebSocket (proxied), conn_init with NOT_READY retry, text_msg.
 */
export function connectViduControl(
  session: ViduLiveSession,
  hooks?: {
    onReady?: () => void;
    onMessage?: (raw: unknown) => void;
    onError?: (err: string) => void;
    onClose?: () => void;
  },
): ViduLiveController {
  let seq = 1;
  let ready = false;
  let closed = false;
  let initAttempts = 0;
  const maxInit = 12;

  const ws = new WebSocket(wsUrl(session.controlWsPath));

  const send = (type: number, payload: Record<string, unknown>) => {
    if (ws.readyState !== WebSocket.OPEN) return;
    const frame = {
      type,
      live_id: session.liveId,
      conn_id: `poker-client`,
      seq_id: seq++,
      payload,
    };
    ws.send(JSON.stringify(frame));
  };

  const tryConnInit = () => {
    if (closed || ready) return;
    initAttempts += 1;
    send(MSG.CONN_INIT, { conn_init: { version: 1 } });
  };

  ws.onopen = () => {
    tryConnInit();
  };

  ws.onmessage = (ev) => {
    let msg: {
      type?: number;
      payload?: {
        conn_init_ack?: {
          success?: boolean;
          error_code?: string;
          error_msg?: string;
        };
        hangup?: { hangup_reason?: string };
      };
    };
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    hooks?.onMessage?.(msg);

    if (msg.type === MSG.CONN_INIT_ACK) {
      const ack = msg.payload?.conn_init_ack;
      if (ack?.success) {
        ready = true;
        hooks?.onReady?.();
        return;
      }
      if (ack?.error_code === "NOT_READY" && initAttempts < maxInit) {
        setTimeout(tryConnInit, 2000 + initAttempts * 200);
        return;
      }
      hooks?.onError?.(
        ack?.error_msg || ack?.error_code || "conn_init failed",
      );
    }
    if (msg.type === MSG.FORCE_HANGUP) {
      hooks?.onClose?.();
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
  };

  ws.onerror = () => hooks?.onError?.("control websocket error");
  ws.onclose = () => {
    closed = true;
    hooks?.onClose?.();
  };

  return {
    session,
    get ready() {
      return ready;
    },
    sendText(content: string) {
      const text = String(content || "").trim().slice(0, 500);
      if (!text || ws.readyState !== WebSocket.OPEN) return;
      send(MSG.TEXT_MSG, {
        text_msg: {
          msg_id: `m-${Date.now()}`,
          content: text,
          timestamp: Date.now(),
        },
      });
    },
    hangup() {
      try {
        send(MSG.CALL_HANGUP, { hangup: { hangup_reason: "user_end" } });
      } catch {
        /* ignore */
      }
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      closed = true;
    },
    destroy() {
      this.hangup();
    },
  };
}

/**
 * Attempt AliRTC join for remote digital character video.
 * Loads Alibaba Web RTC SDK from CDN when available; no-op if load fails.
 */
export async function tryJoinAliRtc(
  rtc: ViduRtc,
  videoEl: HTMLVideoElement,
): Promise<{ ok: boolean; error?: string; leave?: () => void }> {
  if (!rtc?.token || !rtc?.user_id) {
    return { ok: false, error: "missing rtc credentials" };
  }

  // Official Alibaba ART C web pattern — optional dynamic script
  type AliEngine = {
    joinChannel: (token: string, userId: string) => Promise<void>;
    publishLocalAudioStream: (on: boolean) => Promise<void>;
    publishLocalVideoStream: (on: boolean) => Promise<void>;
    setDefaultSubscribeAllRemoteAudioStreams: (on: boolean) => Promise<void>;
    setDefaultSubscribeAllRemoteVideoStreams: (on: boolean) => Promise<void>;
    setRemoteViewConfig: (
      el: HTMLVideoElement,
      userId: string,
      streamType: number,
    ) => void;
    leaveChannel: () => Promise<void>;
    on: (ev: string, cb: (...args: unknown[]) => void) => void;
  };

  try {
    // Prefer global already present (if app loads SDK)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = window as any;
    let AliRtcEngine: { new (): AliEngine } | null =
      g.AliRtcEngine || g.AliRTS || null;

    if (!AliRtcEngine) {
      // Best-effort CDN — if blocked, fall back to share iframe in UI
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement("script");
        s.src =
          "https://g.alicdn.com/apsara-media-box/imp-web-rtc/6.3.0/aliyun-rtc-sdk.js";
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("AliRTC SDK script failed"));
        document.head.appendChild(s);
      }).catch(() => null);
      AliRtcEngine = g.AliRtcEngine || g.AliRTS || null;
    }

    if (!AliRtcEngine) {
      return {
        ok: false,
        error: "AliRTC Web SDK not available — use share avatar fallback",
      };
    }

    const engine = new AliRtcEngine();
    await engine.setDefaultSubscribeAllRemoteAudioStreams(true);
    await engine.setDefaultSubscribeAllRemoteVideoStreams(true);

    engine.on("videoSubscribeStateChanged", (...args: unknown[]) => {
      const userId = String(args[0] ?? "");
      const newState = String(args[2] ?? args[1] ?? "");
      if (newState === "subscribed" || newState === "2") {
        try {
          engine.setRemoteViewConfig(videoEl, userId, 1);
          void videoEl.play().catch(() => undefined);
        } catch {
          /* ignore */
        }
      }
    });

    await engine.joinChannel(rtc.token, rtc.user_id);
    // Mic optional for table buddy — publish audio so VAD can hear if user allows
    try {
      await engine.publishLocalAudioStream(false);
    } catch {
      /* mic denied ok */
    }

    return {
      ok: true,
      leave: () => {
        void engine.leaveChannel().catch(() => undefined);
      },
    };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}
